/**
 * Lightweight User-Agent parser to extract device brand/model, OS, browser.
 * No external dependency. Best-effort — UAs are inherently fuzzy.
 */

export interface DeviceFingerprint {
  device_brand: string;
  device_model: string;
  device_type: 'mobile' | 'tablet' | 'desktop';
  os: string;
  os_version: string;
  browser: string;
}

function detectDeviceType(ua: string): 'mobile' | 'tablet' | 'desktop' {
  if (/iPad|Tablet|PlayBook|Kindle|Silk/i.test(ua)) return 'tablet';
  if (/Mobi|Android|iPhone|iPod/i.test(ua)) return 'mobile';
  return 'desktop';
}

function detectBrowser(ua: string): string {
  if (/Edg\//.test(ua)) return 'Edge';
  if (/OPR\/|Opera/.test(ua)) return 'Opera';
  if (/Chrome\//.test(ua) && !/Edg|OPR/.test(ua)) return 'Chrome';
  if (/Firefox\//.test(ua)) return 'Firefox';
  if (/Safari\//.test(ua) && !/Chrome/.test(ua)) return 'Safari';
  if (/SamsungBrowser/.test(ua)) return 'Samsung Internet';
  return 'Unknown';
}

function detectOS(ua: string): { os: string; os_version: string } {
  let m;
  if ((m = ua.match(/Windows NT ([\d.]+)/))) return { os: 'Windows', os_version: m[1] };
  if ((m = ua.match(/iPhone OS ([\d_]+)/))) return { os: 'iOS', os_version: m[1].replace(/_/g, '.') };
  if ((m = ua.match(/CPU OS ([\d_]+)/))) return { os: 'iPadOS', os_version: m[1].replace(/_/g, '.') };
  if ((m = ua.match(/Mac OS X ([\d_]+)/))) return { os: 'macOS', os_version: m[1].replace(/_/g, '.') };
  if ((m = ua.match(/Android ([\d.]+)/))) return { os: 'Android', os_version: m[1] };
  if (/Linux/.test(ua)) return { os: 'Linux', os_version: '' };
  return { os: 'Unknown', os_version: '' };
}

function detectAppleModel(ua: string): { brand: string; model: string } | null {
  if (/iPhone/.test(ua)) return { brand: 'Apple', model: 'iPhone' };
  if (/iPad/.test(ua)) return { brand: 'Apple', model: 'iPad' };
  if (/iPod/.test(ua)) return { brand: 'Apple', model: 'iPod' };
  if (/Macintosh|Mac OS X/.test(ua)) return { brand: 'Apple', model: 'Mac' };
  return null;
}

function detectAndroidModel(ua: string): { brand: string; model: string } | null {
  // Android UA: "(Linux; Android 11; moto g power (2022)) ..." or "...; SM-S921B Build/..; wv)".
  // Chrome's reduced UA reports the placeholder "K" (real model needs Client Hints).
  const m = ua.match(/Android [\d.]+;\s*(.+?)(?:\s+Build\/|;|\)\s)/i);
  let raw = m ? m[1].trim() : '';
  if (raw === 'K' || /^(Linux|U|wv)$/i.test(raw)) raw = '';
  if (!raw) return { brand: 'Android', model: 'Unknown' };

  const known: Array<[RegExp, string]> = [
    [/^SM-|Samsung|Galaxy/i, 'Samsung'],
    [/^Pixel|^Google /i, 'Google'],
    [/OnePlus|^ONEPLUS|^GM\d/i, 'OnePlus'],
    [/^Mi |Redmi|POCO|Xiaomi|^M\d{4}/i, 'Xiaomi'],
    [/Realme|RMX/i, 'Realme'],
    [/^OPPO|^CPH/i, 'OPPO'],
    [/vivo|^V\d{4}/i, 'Vivo'],
    [/Nothing|^A\d{3}/i, 'Nothing'],
    [/Motorola|^moto |^XT\d/i, 'Motorola'],
    [/Huawei|HUAWEI|Honor/i, 'Huawei'],
    [/Nokia/i, 'Nokia'],
    [/Lenovo/i, 'Lenovo'],
    [/ASUS/i, 'Asus'],
    [/Infinix/i, 'Infinix'],
    [/Tecno/i, 'Tecno'],
    [/iQOO/i, 'iQOO'],
  ];
  for (const [re, brand] of known) {
    if (re.test(raw)) return { brand, model: raw };
  }
  return { brand: 'Android', model: raw };
}

function detectDesktopBrand(os: string): { brand: string; model: string } {
  if (os === 'macOS') return { brand: 'Apple', model: 'Mac' };
  if (os === 'Windows') return { brand: 'PC', model: 'Windows PC' };
  if (os === 'Linux') return { brand: 'PC', model: 'Linux PC' };
  return { brand: 'Unknown', model: 'Unknown' };
}

export function getDeviceFingerprint(): DeviceFingerprint {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  const device_type = detectDeviceType(ua);
  const browser = detectBrowser(ua);
  const { os, os_version } = detectOS(ua);

  let brand = 'Unknown';
  let model = 'Unknown';

  const apple = detectAppleModel(ua);
  if (apple) {
    brand = apple.brand;
    model = apple.model;
  } else if (/Android/.test(ua)) {
    const a = detectAndroidModel(ua);
    if (a) {
      brand = a.brand;
      model = a.model;
    }
  } else {
    const d = detectDesktopBrand(os);
    brand = d.brand;
    model = d.model;
  }

  return { device_brand: brand, device_model: model, device_type, os, os_version, browser };
}

interface UADataHighEntropy {
  model?: string;
  platform?: string;
  platformVersion?: string;
  architecture?: string;
}

interface NavigatorUAData {
  mobile?: boolean;
  platform?: string;
  getHighEntropyValues?: (hints: string[]) => Promise<UADataHighEntropy>;
}

/**
 * Async fingerprint that asks the browser for high-entropy UA Client Hints
 * (model, platformVersion). Required on modern Chrome (v100+) Android where
 * the UA string is reduced and no longer contains the device model.
 *
 * Falls back to the sync UA-parsed fingerprint when UA-CH is unavailable
 * (Safari, Firefox, older Chrome, all iOS browsers).
 */
export async function getDeviceFingerprintAsync(): Promise<DeviceFingerprint> {
  const base = getDeviceFingerprint();

  if (typeof navigator === 'undefined') return base;
  const uaData = (navigator as Navigator & { userAgentData?: NavigatorUAData }).userAgentData;
  if (!uaData?.getHighEntropyValues) return base;

  try {
    const hints = await uaData.getHighEntropyValues(['model', 'platformVersion', 'platform']);

    // Only override when we actually got useful data
    const enhanced: DeviceFingerprint = { ...base };

    if (hints.model && hints.model.trim() && hints.model !== 'K') {
      enhanced.device_model = hints.model.trim();
      // Re-classify brand based on the real model string
      const ua = navigator.userAgent;
      if (/Android/.test(ua)) {
        const known: Array<[RegExp, string]> = [
          [/^SM-|Samsung|Galaxy/i, 'Samsung'],
          [/^Pixel|^Google /i, 'Google'],
          [/OnePlus|^ONEPLUS|^GM\d|^CPH2|^IN\d|^LE\d/i, 'OnePlus'],
          [/^Mi |Redmi|POCO|Xiaomi|^M\d{4}|^2\d{3}/i, 'Xiaomi'],
          [/Realme|RMX/i, 'Realme'],
          [/^OPPO|^CPH/i, 'OPPO'],
          [/vivo|^V\d{4}/i, 'Vivo'],
          [/Nothing|^A\d{3}/i, 'Nothing'],
          [/Motorola|^moto |^XT\d/i, 'Motorola'],
          [/Huawei|HUAWEI|Honor/i, 'Huawei'],
          [/iQOO/i, 'iQOO'],
          [/Infinix/i, 'Infinix'],
          [/Tecno/i, 'Tecno'],
        ];
        const m = known.find(([re]) => re.test(enhanced.device_model));
        enhanced.device_brand = m ? m[1] : 'Android';
      }
    }

    if (hints.platformVersion && hints.platformVersion.trim()) {
      enhanced.os_version = hints.platformVersion.trim();
    }

    return enhanced;
  } catch {
    return base;
  }
}
